import React, { useEffect } from 'react';
import { groupBy } from 'lodash';
import { CSVLink } from 'react-csv';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import TertiaryButton from 'commons/src/components/buttons/TertiaryButton';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import ModalWrapper from 'commons/src/components/modals/ModalWrapper';
import deviceHealthStatus from 'commons/src/models/deviceHealthStatus';
import { fetchHealthStatusIssueData } from '../../../actions/healthStatusActions';
import { getBusinessIntercomArticleLink } from '../../../constants';
import { IntercomArticleId } from '../../../models/commonEnums';
import { Store } from '../../../reducers';
import CardIssueTable from './CardIssueTable';
import styles from './StatusModal.module.scss';
import { generateCSVData, generateRows, issueCardHeaders, IssueColumn } from './statusModalCommonFunctions';

type ModalProps = {
    open: boolean;
    toggle: () => void;
    displayedNumber: number;
    header: string;
};

const OfflineStatusModal = ({ open, toggle, displayedNumber, header }: ModalProps): React.ReactElement => {
    const { t: txt } = useTranslation();

    const dispatch = useDispatch();

    useEffect(() => {
        if (open) {
            dispatch(fetchHealthStatusIssueData(deviceHealthStatus.offline));
        }
    }, [open]);

    const offlineDevices = useSelector((store: Store) => store.buildingsHealthStatus.offlineDevices);

    const onClose = (): void => {
        toggle();
    };

    const groupedDevices = groupBy(offlineDevices?.devices, 'locationId');

    const tableHeaders = issueCardHeaders.filter(h =>
        [
            IssueColumn.Buildings,
            IssueColumn.Devices,
            IssueColumn.Name,
            IssueColumn.LastSeen,
            IssueColumn.Connection,
            IssueColumn.Battery,
        ].includes(h.key)
    );

    const selectedColumns = tableHeaders.map(h => h.key);

    const tableRows = Object.entries(groupedDevices).flatMap(([, devices]) =>
        generateRows(devices, selectedColumns, false)
    );

    const allColumns = issueCardHeaders.map(h => h.key);

    const csvRows = Object.entries(groupedDevices).flatMap(([, devices]) => generateRows(devices, allColumns, true));

    const offlineHeader = (
        <div className={styles.header}>
            <div className={styles.headerText}>
                <span className={styles.textBig}>{displayedNumber}</span>
                <span>{txt(header)}</span>
                <MaterialIcon name="wifi_off" extraClass={styles.redIcon} />
            </div>
            <TertiaryButton onClick={onClose} noColor icon={<MaterialIcon name="close" />} testAttr="close" />
        </div>
    );

    return (
        <ModalWrapper isOpen={open} onClose={onClose} customHeader={offlineHeader} size="large">
            <div className={styles.modalContent}>
                <div>
                    <Trans i18nKey="BuildingStatus.HelpDescription">
                        <Link to={getBusinessIntercomArticleLink(IntercomArticleId.TROUBLESHOOTING_OFFLINE_HUB)}>
                            {txt('BuildingStatus.OfflineHubs')}
                        </Link>
                        <Link to={getBusinessIntercomArticleLink(IntercomArticleId.TROUBLESHOOTING_OFFLINE_DEVICE)}>
                            {txt('BuildingStatus.OfflineDevices')}
                        </Link>
                    </Trans>
                </div>
                <div className={styles.tableWrapper}>
                    <CardIssueTable headers={tableHeaders} rows={tableRows} />
                </div>
                <CSVLink
                    data={generateCSVData(issueCardHeaders, csvRows)}
                    separator=";"
                    filename="Offline_hubs+devices.csv"
                    className={styles.downloadButton}
                >
                    {txt('BuildingStatus.DownloadList')}
                </CSVLink>
            </div>
        </ModalWrapper>
    );
};

export default OfflineStatusModal;
